import { translate } from 'shared/lib/i18n.tsx';
import { defaultRemoteViewLayout, type RemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import {
  getRemoteCalibStandZoneMetrics,
  getRemoteCalibStartFruitMetrics,
} from 'shared/lib/tv-game/remoteCalibStandZone.ts';

import type { RemoteDisplayState } from '../../../domain/RemoteDisplayState.ts';
import type { Particle } from '../../../domain/Particle.ts';
import { REMOTE_FRUIT_PRELOAD_URLS } from '../../../media/remoteFruitSprites.ts';
import { getFruitImage, preloadFruitImages } from '../../fruitImageCache.ts';
import { drawImageCover, shouldDrawRemoteFruitDebugRing } from '../room/primitives.ts';
import { drawHudStrip, drawSideStrips } from '../room/letterbox.ts';
import { drawLiveSkeletonPts, drawBladeTrails } from '../room/skeleton.ts';
import { drawRemoteCalibrationStandZone } from '../room/calibrationShared.ts';
import { useLighterCanvasEffects, wrapCanvasText } from '../room/canvasHelpers.ts';
import { getTvQualityConfig, type TvQualityConfig } from '../../qualityProfile.ts';

const FRUIT_NINJA_BG = '/media/fruitSliceBg.jpg';

interface BombFlash {
  x: number;
  y: number;
  startTime: number;
  /** Closure-cached gradient — построен один раз при спавне, переиспользуется на все ~42 кадра. */
  gradient?: CanvasGradient;
}

const BOMB_FLASH_DURATION_MS = 500;
const MAX_BOMB_FLASHES = 4;

export class FruitNinjaCanvasLayer {
  private readonly ctx: CanvasRenderingContext2D;

  private readonly bgImage: HTMLImageElement;

  private bombFlashes: BombFlash[] = [];
  private readonly juiceBuckets = new Map<string, { color: string; alpha: number; xs: number[]; ys: number[]; rs: number[] }>();

  private quality: TvQualityConfig;

  /** Backdrop предрендерим один раз на размер буфера → экономим gradient/drawImageCover на каждый кадр. */
  private backdropCache: HTMLCanvasElement | null = null;

  private backdropCacheKey = '';

  constructor(
    private readonly canvas: HTMLCanvasElement,
    quality: TvQualityConfig = getTvQualityConfig('high'),
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get 2D canvas context');
    this.ctx = ctx;
    // src фона и спрайты фруктов — в preload(): грузятся только когда выбрана эта игра.
    const img = new Image();
    this.bgImage = img;
    img.onload = () => {
      // Фон догрузился позже → инвалидируем кэш чтобы пересобрать с реальной картинкой.
      this.backdropCache = null;
      this.backdropCacheKey = '';
    };
    this.quality = quality;
  }

  preload(): void {
    if (!this.bgImage.src) this.bgImage.src = FRUIT_NINJA_BG;
    preloadFruitImages(REMOTE_FRUIT_PRELOAD_URLS);
  }

  setQualityConfig(q: TvQualityConfig): void {
    this.quality = q;
  }

  triggerBombFlash(x: number, y: number): void {
    this.bombFlashes.push({ x, y, startTime: Date.now() });
    if (this.bombFlashes.length > MAX_BOMB_FLASHES) {
      this.bombFlashes.splice(0, this.bombFlashes.length - MAX_BOMB_FLASHES);
    }
  }

  clearCanvas(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  render(state: RemoteDisplayState): void {
    if (
      state.phase !== 'playing'
      && state.phase !== 'gameover'
      && state.phase !== 'calibration'
    ) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      return;
    }

    const W = this.canvas.width;
    const H = this.canvas.height;
    const { ctx } = this;
    ctx.clearRect(0, 0, W, H);
    const layout = state.remoteView ?? defaultRemoteViewLayout(W, H);
    this.drawFruitRemoteBackdrop(W, H, layout);

    if (state.phase === 'calibration') {
      this.drawCalibrationScreen(state, W, H);
      return;
    }

    drawHudStrip(this.ctx, state, W);
    drawSideStrips(this.ctx, state, H, this.canvas.width);
    if (state.splitTwoPlayers) {
      this.drawCenterUiDivider(layout);
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(layout.offsetX, layout.offsetY, layout.gameWidth, layout.gameHeight);
    ctx.clip();
    this.drawLiveSkeleton(state, layout);
    this.drawJuiceParticles(state.particles);
    this.drawRemoteFruits(state, W, H);
    drawBladeTrails(this.ctx, this.canvas.width, state.bladeTrails, layout, useLighterCanvasEffects(this.canvas), state.p1PlayerId, state.p2PlayerId);
    this.drawBombFlashes(W, H);
    ctx.restore();
  }

  private drawCenterUiDivider(layout: RemoteViewLayout): void {
    const ctx = this.ctx;
    const midX = layout.offsetX + layout.gameWidth / 2;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = Math.max(2, layout.gameWidth * 0.0015);
    ctx.shadowBlur = 14;
    ctx.shadowColor = 'rgba(255,220,100,0.55)';
    ctx.setLineDash([10, 12]);
    ctx.beginPath();
    ctx.moveTo(midX, layout.offsetY + Math.max(8, layout.gameHeight * 0.02));
    ctx.lineTo(midX, layout.offsetY + layout.gameHeight - Math.max(8, layout.gameHeight * 0.02));
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  private drawRemoteFruits(state: RemoteDisplayState, W: number, H: number): void {
    const { ctx } = this;
    const layout = state.remoteView ?? defaultRemoteViewLayout(W, H);
    const fontPx = layout.fruitFontPx;
    const ringR = layout.fruitRadiusPx;
    const showDebugRing = shouldDrawRemoteFruitDebugRing();
    const useShadow = this.quality.fruitShadowBlur;

    // Single save/restore wrapping all fruits; use setTransform to reset between items.
    ctx.save();
    for (const f of state.fruits) {
      const px = layout.offsetX + f.nx * layout.gameWidth;
      const py = layout.offsetY + f.ny * layout.gameHeight;
      const d = ringR * 2;
      const img = getFruitImage(f.pngSrc);

      if (f.isHit && f.nativeHalves) {
        const h1 = f.nativeHalves.half1;
        const h2 = f.nativeHalves.half2;
        const x1 = layout.offsetX + h1.nx * layout.gameWidth;
        const y1 = layout.offsetY + h1.ny * layout.gameHeight;
        const x2 = layout.offsetX + h2.nx * layout.gameWidth;
        const y2 = layout.offsetY + h2.ny * layout.gameHeight;
        const halfSrc = f.halfPngSrc;
        const halfImg = halfSrc ? getFruitImage(halfSrc) : img;
        const halfD = d * 0.7;
        if (halfImg.complete && halfImg.naturalWidth > 0) {
          this.drawRemoteHalfSprite(ctx, halfImg, x1, y1, h1.rotationRad, halfD, 'first');
          this.drawRemoteHalfSprite(ctx, halfImg, x2, y2, h2.rotationRad, halfD, 'second');
        } else if (img.complete && img.naturalWidth > 0) {
          this.drawRemoteHalfClipped(ctx, img, x1, y1, h1.rotationRad, halfD, 'top');
          this.drawRemoteHalfClipped(ctx, img, x2, y2, h2.rotationRad, halfD, 'bottom');
        }
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        if (useShadow) { ctx.shadowBlur = 0; ctx.shadowColor = 'transparent'; }
        continue;
      }

      if (f.isHit) continue;

      if (showDebugRing) {
        ctx.beginPath();
        ctx.arc(px, py, ringR, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0, 220, 255, 0.9)';
        ctx.lineWidth = Math.max(2, fontPx * 0.012);
        ctx.setLineDash([]);
        ctx.stroke();
      }

      ctx.translate(px, py);
      ctx.rotate(f.rotationRad);
      if (useShadow) {
        if (f.isBomb) {
          ctx.shadowBlur = 24;
          ctx.shadowColor = 'rgba(255, 60, 40, 0.55)';
        } else {
          ctx.shadowBlur = 16;
          ctx.shadowColor = 'rgba(60, 200, 80, 0.45)';
        }
      }
      if (img.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, -d / 2, -d / 2, d, d);
      } else {
        ctx.fillStyle = f.isBomb ? 'rgba(255, 60, 40, 0.4)' : 'rgba(80, 200, 100, 0.35)';
        ctx.beginPath();
        ctx.arc(0, 0, ringR, 0, Math.PI * 2);
        ctx.fill();
      }
      // Reset transform + shadow for next fruit without save/restore overhead.
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      if (useShadow) { ctx.shadowBlur = 0; ctx.shadowColor = 'transparent'; }
    }
    ctx.restore();
  }

  private drawRemoteHalfSprite(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    px: number,
    py: number,
    rotationRad: number,
    d: number,
    variant: 'first' | 'second',
  ): void {
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(rotationRad);
    if (variant === 'first') {
      ctx.rotate(Math.PI);
    }
    ctx.drawImage(img, -d / 2, -d / 2, d, d);
    ctx.restore();
  }

  private drawRemoteHalfClipped(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    px: number,
    py: number,
    rotationRad: number,
    d: number,
    half: 'top' | 'bottom',
  ): void {
    const w = d;
    const h = d;
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(rotationRad);
    ctx.beginPath();
    if (half === 'top') {
      ctx.rect(-w, -h, w * 2, h);
    } else {
      ctx.rect(-w, 0, w * 2, h);
    }
    ctx.clip();
    if (half === 'bottom') {
      ctx.rotate(Math.PI);
    }
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.restore();
  }

  private drawCalibrationScreen(state: RemoteDisplayState, W: number, H: number): void {
    const layout = state.remoteView ?? defaultRemoteViewLayout(W, H);
    const { ctx } = this;
    const {
      offsetX, offsetY, gameWidth, gameHeight,
    } = layout;

    ctx.save();
    ctx.beginPath();
    ctx.rect(offsetX, offsetY, gameWidth, gameHeight);
    ctx.clip();

    const overlay = ctx.createLinearGradient(offsetX, offsetY, offsetX, offsetY + gameHeight);
    overlay.addColorStop(0, 'rgba(4, 12, 38, 0.90)');
    overlay.addColorStop(0.6, 'rgba(7, 20, 58, 0.86)');
    overlay.addColorStop(1, 'rgba(4, 8, 28, 0.92)');
    ctx.fillStyle = overlay;
    ctx.fillRect(offsetX, offsetY, gameWidth, gameHeight);

    this.drawLiveSkeleton(state, layout);

    if (!state.splitTwoPlayers) {
      this.drawCalibZoneWithFruit(layout);
      ctx.restore();
      return;
    }

    this.drawCenterUiDivider(layout);
    const halfWidth = layout.gameWidth / 2;
    const leftLayout = {
      ...layout,
      gameWidth: halfWidth,
    };
    const rightLayout = {
      ...layout,
      offsetX: layout.offsetX + halfWidth,
      gameWidth: halfWidth,
    };
    this.drawCalibZoneWithFruit(leftLayout, 'P1');
    this.drawCalibZoneWithFruit(rightLayout, 'P2');
    ctx.restore();
  }

  private drawCalibZoneWithFruit(layout: RemoteViewLayout, badge?: 'P1' | 'P2'): void {
    const { ctx } = this;
    const { gameHeight, gameWidth } = layout;
    drawRemoteCalibrationStandZone(ctx, layout, 'upper');
    const z = getRemoteCalibStandZoneMetrics(layout, 'upper');
    const {
      cx, boxX, boxY, boxW, borderW,
    } = z;

    // Larger, more readable font — boxW / 14 so it scales up in 2-player split view
    const instrPx = Math.round(Math.min(gameHeight * 0.034, boxW / 14, 46));
    const instrPadX = boxW * 0.05;
    const sw = boxW - instrPadX * 2;
    const lineGap = instrPx * 1.45;
    const line1 = translate('calib.hint.waist_up');
    const line2 = translate('calib.hint.slice_to_start');
    ctx.save();
    // Must set font before measuring for wrap
    ctx.font = `600 ${instrPx}px Arial, sans-serif`;
    const wrapped1 = wrapCanvasText(ctx, line1, sw);
    const wrapped2 = wrapCanvasText(ctx, line2, sw);
    const allLines = [...wrapped1, ...wrapped2];
    const stripPadV = instrPx * 0.55;
    const stripH = allLines.length * lineGap + stripPadV * 2;
    const insetTop = borderW * 2.2 + Math.max(10, gameHeight * 0.015);
    ctx.fillStyle = 'rgba(0,0,0,0.58)';
    ctx.beginPath();
    const stripR = Math.min(14, boxW * 0.04);
    const sx = boxX + instrPadX;
    const sy = boxY + insetTop;
    const sh = stripH;
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(sx, sy, sw, sh, stripR);
    } else {
      ctx.rect(sx, sy, sw, sh);
    }
    ctx.fill();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowBlur = 14;
    ctx.shadowColor = 'rgba(0,0,0,0.92)';
    ctx.fillStyle = '#ffe566';
    const firstLineY = sy + stripPadV + lineGap * 0.5;
    allLines.forEach((line, i) => {
      ctx.fillText(line, cx, firstLineY + i * lineGap);
    });
    if (badge) {
      ctx.font = `900 ${Math.max(16, instrPx * 0.9)}px Arial, sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.fillText(badge, cx, sy - Math.max(10, instrPx * 0.7));
    }
    ctx.restore();

    const startFruit = getRemoteCalibStartFruitMetrics(layout, 'upper');
    const fruitR = Math.round(startFruit.r);
    const fruitCx = startFruit.cx;
    const fruitCy = startFruit.cy;
    const fruitImg = getFruitImage('/media/watermelon_whole.png');
    const fruitPulse = this.quality.calibPulse ? 0.88 + 0.12 * Math.sin(Date.now() / 260) : 1;

    ctx.save();
    if (this.quality.calibPulse) {
      ctx.shadowBlur = 35;
      ctx.shadowColor = 'rgba(255,220,60,0.85)';
    }
    if (fruitImg.complete && fruitImg.naturalWidth > 0) {
      ctx.globalAlpha = fruitPulse;
      ctx.drawImage(
        fruitImg,
        fruitCx - fruitR * fruitPulse,
        fruitCy - fruitR * fruitPulse,
        fruitR * 2 * fruitPulse,
        fruitR * 2 * fruitPulse,
      );
    } else {
      ctx.fillStyle = `rgba(60,200,80,${fruitPulse})`;
      ctx.beginPath();
      ctx.arc(fruitCx, fruitCy, fruitR * fruitPulse, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    const lblPx = Math.round(Math.min(fruitR * 0.55, gameWidth * 0.026, 36));
    ctx.save();
    ctx.font = `900 ${lblPx}px 'Arial Black', Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowBlur = 18;
    ctx.shadowColor = 'rgba(0,0,0,1)';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(translate('calib.start'), fruitCx, fruitCy);
    ctx.restore();
  }

  private drawBombFlashes(W: number, H: number): void {
    const { ctx } = this;
    const now = Date.now();
    let wf = 0;
    for (let i = 0; i < this.bombFlashes.length; i++) {
      if (now - this.bombFlashes[i]!.startTime < BOMB_FLASH_DURATION_MS) {
        if (wf !== i) this.bombFlashes[wf] = this.bombFlashes[i]!;
        wf++;
      }
    }
    this.bombFlashes.length = wf;

    for (const flash of this.bombFlashes) {
      const elapsed = now - flash.startTime;
      const flashProgress = Math.min(1, elapsed / BOMB_FLASH_DURATION_MS);
      const flashAlpha = 0.38 * (1 - flashProgress) ** 2;
      if (flashAlpha > 0.005) {
        ctx.save();
        ctx.globalAlpha = flashAlpha;
        ctx.fillStyle = 'rgba(255, 80, 0, 1)';
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
      }
    }
  }

  private drawFruitRemoteBackdrop(W: number, H: number, layout: RemoteViewLayout): void {
    const { ctx } = this;
    const cacheKey = `${W}x${H}:${layout.offsetX}:${layout.offsetY}:${layout.gameWidth}:${layout.gameHeight}:${this.bgImage.complete ? '1' : '0'}`;
    if (this.backdropCache && this.backdropCacheKey === cacheKey) {
      ctx.drawImage(this.backdropCache, 0, 0);
      return;
    }

    const off = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    if (off) {
      off.width = W;
      off.height = H;
      const octx = off.getContext('2d');
      if (octx) {
        this.paintBackdrop(octx, W, H, layout);
        this.backdropCache = off;
        this.backdropCacheKey = cacheKey;
        ctx.drawImage(off, 0, 0);
        return;
      }
    }
    this.paintBackdrop(ctx, W, H, layout);
  }

  private paintBackdrop(
    ctx: CanvasRenderingContext2D,
    W: number,
    H: number,
    layout: RemoteViewLayout,
  ): void {
    ctx.fillStyle = '#0a0015';
    ctx.fillRect(0, 0, W, H);
    const img = this.bgImage;
    const {
      offsetX, offsetY, gameWidth, gameHeight,
    } = layout;
    if (img.complete && img.naturalWidth > 0) {
      drawImageCover(ctx, img, offsetX, offsetY, gameWidth, gameHeight);
      return;
    }
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, '#0d0025');
    grad.addColorStop(1, '#1a0015');
    ctx.fillStyle = grad;
    ctx.fillRect(offsetX, offsetY, gameWidth, gameHeight);
  }

  /**
   * Одним проходом рисуем все juice-частицы, сгруппированные по цвету:
   * один fillStyle/globalAlpha-бакет = один ctx.fill() с множеством arc'ов.
   * Buckets реиспользуются между кадрами — xs/ys/rs очищаем через .length=0, не пересоздаём.
   */
  private drawJuiceParticles(particles: Particle[]): void {
    const { ctx } = this;
    const buckets = this.juiceBuckets;

    // Reset counts without deallocating inner arrays.
    buckets.forEach((b) => { b.xs.length = 0; b.ys.length = 0; b.rs.length = 0; });

    for (const p of particles) {
      if (p.kind !== 'juice') continue;
      const alphaBucket = Math.round(p.alpha * 10) / 10;
      const key = `${p.color}|${alphaBucket}`;
      let b = buckets.get(key);
      if (!b) {
        b = { color: p.color, alpha: alphaBucket, xs: [], ys: [], rs: [] };
        buckets.set(key, b);
      }
      b.xs.push(p.x);
      b.ys.push(p.y);
      b.rs.push(p.radius);
    }

    ctx.save();
    buckets.forEach((b) => {
      if (b.xs.length === 0) return;
      ctx.globalAlpha = b.alpha;
      ctx.fillStyle = b.color;
      ctx.beginPath();
      for (let i = 0; i < b.xs.length; i++) {
        const x = b.xs[i]!;
        const y = b.ys[i]!;
        const r = b.rs[i]!;
        ctx.moveTo(x + r, y);
        ctx.arc(x, y, r, 0, Math.PI * 2);
      }
      ctx.fill();
    });
    ctx.restore();
  }

  private drawLiveSkeleton(state: RemoteDisplayState, layout: RemoteViewLayout): void {
    drawLiveSkeletonPts(
      this.ctx,
      state.calibNormPoints,
      layout,
      state.phase === 'playing',
      80,
      220,
      255,
      useLighterCanvasEffects(this.canvas),
    );
    if (state.calibNormPointsP2.size > 0) {
      drawLiveSkeletonPts(
        this.ctx,
        state.calibNormPointsP2,
        layout,
        state.phase === 'playing',
        255,
        120,
        200,
        useLighterCanvasEffects(this.canvas),
      );
    }
  }
}
